import { useEffect, useRef } from "react";

/** Which of the two a keystroke asks for. */
export type UndoCommand = "undo" | "redo";

/** The parts of a keystroke {@link undoCommandOf} reads. */
type Keystroke = Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">;

/**
 * Rückgängig or Wiederholen, by the keys every office program uses — or
 * `null` for any other keystroke.
 *
 * ⌘Z and Strg+Z take back; ⇧⌘Z, Strg+Umschalt+Z and Strg+Y put back. The
 * letter is read off `key`, not `code`: on a German keyboard Z and Y trade
 * places, and a person presses the key that *says* Z. ⌘Y is left alone — on
 * a Mac it opens the browser's history, and nobody there expects it to redo.
 * Alt is never part of it: Alt+Strg is AltGr on Windows, which types
 * characters.
 */
export function undoCommandOf(event: Keystroke): UndoCommand | null {
  if (event.altKey || !(event.metaKey || event.ctrlKey)) return null;
  const letter = event.key.toLowerCase();
  if (letter === "z") return event.shiftKey ? "redo" : "undo";
  if (letter === "y" && event.ctrlKey && !event.metaKey && !event.shiftKey) return "redo";
  return null;
}

/**
 * Whether a keystroke was meant for a field somebody is typing into — whose
 * own undo (the last letters typed) wins over the board's.
 */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable || target.closest('[contenteditable]:not([contenteditable="false"])') !== null) {
    return true;
  }
  return target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.tagName === "SELECT";
}

/**
 * Whether a modal dialog stands over the page — the question it asks is
 * about the board as it stands, and no step may change it underneath.
 *
 * Read off the document, not off the focus: a click on the backdrop or on
 * the dialog's own text drops the focus to `<body>`, and a toast's button is
 * outside the dialog's box by design (the toast host is portalled above the
 * backdrop). One answer for every way a step can be asked for — the keys,
 * the board's buttons and a laying's toast.
 */
export function modalDialogOpen(): boolean {
  return typeof document !== "undefined" && document.querySelector('[role="dialog"][aria-modal="true"]') !== null;
}

/** A Mac writes ⌘, everything else Strg — K60 works on Windows. */
function onMac(): boolean {
  return typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
}

/**
 * The two shortcuts as the keyboard in front of the person labels them, one
 * key per entry, for `Kbd`. Windows shows Strg+Y for Wiederholen, the key
 * Office teaches there; Strg+Umschalt+Z works as well, and is not shown.
 */
export function undoKeys(mac: boolean = onMac()): { undo: string[]; redo: string[] } {
  return mac ? { undo: ["⌘", "Z"], redo: ["⇧", "⌘", "Z"] } : { undo: ["Strg", "Z"], redo: ["Strg", "Y"] };
}

/**
 * Runs one Rückgängig or Wiederholen and keeps the keyboard's place — or,
 * where the step took that place away, puts the focus on `fallback(lost)`.
 *
 * The place goes in two ways. The element the focus stood on was part of
 * the step: ⌘Z on a grating that was just laid takes the grating, and its
 * grip with it. Or the focus stood on the button that has just gone grey:
 * Wiederholen after the one step Rückgängig took, Rückgängig at the bottom of
 * the stack — and a disabled button drops the focus to `<body>`. Either way
 * Tab would start again at the top of the page.
 *
 * Checked a task later, once React has drawn the step (a store change from a
 * native listener is rendered in a microtask). A focus somebody put
 * elsewhere in the meantime stays where they put it.
 */
export function keepingFocus(
  step: () => void,
  fallback: (lost: Element) => HTMLElement | SVGElement | null,
): void {
  const had = typeof document === "undefined" ? null : document.activeElement;
  step();
  if (had === null || had === document.body) return;
  window.setTimeout(() => {
    const now = document.activeElement;
    if (now !== null && now !== document.body && now !== had) return;
    const gone = !had.isConnected || (had instanceof HTMLButtonElement && had.disabled);
    if (gone) fallback(had)?.focus();
  }, 0);
}

interface UndoKeys {
  /** Whether Rückgängig would do anything — the same answer the button's `disabled` gives. */
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
}

/**
 * ⌘Z/Strg+Z and ⇧⌘Z/Strg+Y on a drawing board — the button's twin, never
 * more than the button.
 *
 * On `document`, bubbling, like the board's other page-wide keys: whatever
 * already answered the keystroke (`defaultPrevented`) keeps it. It stays out
 * of the way of three things:
 *
 * - **a field somebody is typing in** — an input, a select, a textarea, rich
 *   text: there ⌘Z takes back the last letters, as everywhere else;
 * - **a dialog or a menu over the board** — the question is about the board
 *   as it stands, and must not watch it change underneath. A modal dialog
 *   counts wherever the keystroke comes from: a click on its backdrop or on
 *   its own text drops the focus to `<body>`, outside the box, and „Freigabe
 *   wird ungültig" would otherwise watch a Stand being written under it;
 * - **a pointer that is down** — the button cannot be pressed in the middle
 *   of a drag, and the key must not either: the drag's next frame would fold
 *   into the step before it and take that one with it.
 *
 * Where the button would be grey (`canUndo`/`canRedo` false) nothing happens
 * and the keystroke goes on to the browser untouched.
 *
 * Re-bound every render, as the review screen's keys are, so the handlers
 * always see the board as it stands.
 */
export function useUndoKeys({ canUndo, canRedo, onUndo, onRedo }: UndoKeys): void {
  const pointerDown = useRef(false);

  useEffect(() => {
    // The main button only: that is what drags. A right-click that opens the
    // browser's own menu may never see its `pointerup`, and must not leave
    // the keys dead behind it.
    const press = (event: PointerEvent) => {
      if (event.button === 0) pointerDown.current = true;
    };
    const release = () => {
      pointerDown.current = false;
    };
    // Every move says whether the main button is still down. A native
    // select's popup takes the release for itself — the page sees the press
    // and never its `pointerup` — and the keys would stay dead until the
    // next click somewhere.
    const move = (event: PointerEvent) => {
      pointerDown.current = (event.buttons & 1) === 1;
    };
    document.addEventListener("pointerdown", press, true);
    document.addEventListener("pointermove", move, true);
    document.addEventListener("pointerup", release, true);
    document.addEventListener("pointercancel", release, true);
    // A release outside the window never arrives as `pointerup`.
    window.addEventListener("blur", release);
    return () => {
      document.removeEventListener("pointerdown", press, true);
      document.removeEventListener("pointermove", move, true);
      document.removeEventListener("pointerup", release, true);
      document.removeEventListener("pointercancel", release, true);
      window.removeEventListener("blur", release);
    };
  }, []);

  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.defaultPrevented) return;
      const command = undoCommandOf(event);
      if (command === null || isTypingTarget(event.target) || pointerDown.current) return;
      if (event.target instanceof Element && event.target.closest('[role="dialog"], [role="menu"]')) return;
      if (modalDialogOpen()) return;
      if (command === "undo" ? !canUndo : !canRedo) return;
      event.preventDefault();
      if (command === "undo") onUndo();
      else onRedo();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });
}
